// App.tsx — top-level shell. Gates on the OIDC session via useAuth, hosts
// the nav, and switches between the metrics, sessions and MCP pages.
// Identity comes from /api/me (driven by the thirdeye_auth cookie that
// /api/oidc/callback sets server-side).
//
// Routing: react-router-dom drives the browser URL. Each top-level page
// has its own path so deep-linking, bookmarks, and back/forward all work.
// The Go server already SPA-falls-back any unknown path to index.html
// (managerserver.serveFrontend), so client-side routes resolve cleanly
// on hard reloads too.
import React from 'react';
import { Container } from 'react-bootstrap';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { LoginForm, useAuth, useLoginConfig } from 'common';

import { apiClient } from './api';
import McpServers from './components/McpServers';
import Dreams from './components/Dreams';
import { Metrics } from './components/Metrics';
import Navigation from './components/Navigation';
import { Sessions } from './components/Sessions';

import './index.css';

// AuthenticatedShell renders the nav + page area for a signed-in user.
// Pulled into its own component so the router context (NavLink, useLocation)
// is available to Navigation, which highlights the active tab from the URL.
const AuthenticatedShell: React.FC<{
  email?: string;
  photoUrl?: string;
}> = ({ email, photoUrl }) => {
  // Every route is full-bleed (DESIGN.md C-LAYOUT-1) — the 1400px cap that
  // used to sit on the reading pages spent the display on gutters and never
  // fired on the small screens it was supposed to help.
  //
  // The sessions manager goes one step further and drops the container's
  // gutters as well: it is a dual pane whose halves own their own scroll
  // regions and already carry their own padding, so the outer padding would
  // just be inset twice.
  const { pathname } = useLocation();
  const flushGutters = pathname.startsWith('/sessions');

  return (
  <div className="d-flex flex-column min-vh-100">
    <Navigation
      userEmail={email}
      userPhotoUrl={photoUrl}
      onLogout={() => {
        window.location.href = '/api/oidc/logout';
      }}
    />

    <Container fluid className={flushGutters ? 'py-4 px-0' : 'py-4'}>
      <main className="h-100">
        <Routes>
          <Route path="/" element={<Metrics />} />
          <Route path="/sessions" element={<Sessions />} />
          <Route path="/mcp" element={<McpServers />} />
          <Route path="/dreams" element={<Dreams />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </Container>

    <footer className="bg-dark py-3 text-center mt-auto">
      <div className="container-fluid">
        <small className="text-muted text-uppercase">&copy; 2026 Third Eye. Alpha v0.2.0</small>
      </div>
    </footer>
  </div>
  );
};

const App: React.FC = () => {
  const auth = useAuth({ client: apiClient });
  const loginConfig = useLoginConfig();

  if (auth.loading) {
    return (
      <div className="d-flex flex-column min-vh-100 justify-content-center align-items-center">
        <div className="text-muted">Loading…</div>
      </div>
    );
  }

  if (!auth.authenticated) {
    // Sign-in happens here rather than at authz: LoginForm posts credentials
    // to our own /api/oidc/password-login when the backend supports it, and
    // falls back to the redirect button this used to be when it doesn't. The
    // probe has to settle first or the button would flash before the form.
    if (loginConfig.loading) {
      return (
        <div className="d-flex flex-column min-vh-100 justify-content-center align-items-center">
          <div className="text-muted">Loading…</div>
        </div>
      );
    }
    return (
      <LoginForm
        title="ThirdEye"
        returnTo={window.location.pathname || '/'}
        password={loginConfig.password}
        providers={loginConfig.providers}
        issuerLabel={loginConfig.issuer}
        onSuccess={() => { void auth.refresh(); }}
      />
    );
  }

  return (
    <BrowserRouter>
      <AuthenticatedShell email={auth.email} photoUrl={auth.photoUrl} />
    </BrowserRouter>
  );
};

export default App;
